import { Moon, Sun } from "lucide-react";
import { useTheme } from "../hooks/useTheme";

interface ThemeToggleProps {
  /**
   * Controlled mode (#3534): a surface that owns its own theme state -- the
   * client portal persists under `portal-theme`, never `crm-theme` -- passes
   * its `theme` + `onToggle`. Omit both for the CRM's own `useTheme`.
   */
  theme?: "light" | "dark";
  onToggle?: () => void;
  /** Extra classes (e.g. a 44px touch target on the portal header). */
  className?: string;
}

function CrmThemeToggle({ className }: { className?: string }) {
  const { theme, toggleTheme } = useTheme();
  return <ThemeToggleButton theme={theme} onToggle={toggleTheme} className={className} />;
}

function ThemeToggleButton({
  theme,
  onToggle,
  className = "",
}: {
  theme: "light" | "dark";
  onToggle: () => void;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      className={`p-2 rounded-lg hover:bg-[var(--color-bg-page)] transition-colors ${className}`.trim()}
      aria-label={`Switch to ${theme === "light" ? "dark" : "light"} mode`}
      title={`Switch to ${theme === "light" ? "dark" : "light"} mode`}
    >
      {theme === "light" ? (
        <Moon className="h-5 w-5 text-[var(--color-text-secondary)]" />
      ) : (
        <Sun className="h-5 w-5 text-yellow-400" />
      )}
    </button>
  );
}

export function ThemeToggle({ theme, onToggle, className }: ThemeToggleProps = {}) {
  if (theme && onToggle) {
    return <ThemeToggleButton theme={theme} onToggle={onToggle} className={className} />;
  }
  return <CrmThemeToggle className={className} />;
}
